import React, { useEffect, useState } from 'react'
import { Breadcrumb } from '../../../../components/ui/breadcrumb'
import toast, { Toaster } from 'react-hot-toast'
import { Label } from '../../../../components/ui/label'
import { Combobox } from '../../../../components/ui/combobox'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import useAuth from '../../../../hooks/useAuth'
import { Button } from '../../../../components/ui/button'
import { Loader2 } from 'lucide-react'
import { getCustomerList } from '../../../../lib/fetch/customer'
import Detail from './customer/Detail'
import { useTranslation } from 'react-i18next'
import useCustomerSettingsStore from '../../../../zustand/useCustomerSettingsStore'

const CustomerSettings = () => {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const { t } = useTranslation()
  const partners = useSelector((state: RootState) => state.partners)
  const {
    selected_partner, setselected_partner,
    selected_customer, setselected_customer,
    customers_list, setcustomers_list,
  } = useCustomerSettingsStore()

  const [is_loading_customers, setis_loading_customers] = useState(false)

  useEffect(() => {
    if (selected_partner !== null) return
    const filtered = Array.isArray(partners) ? partners : []
    if (filtered.length === 1) {
      handlePickPartner(filtered[0].relnr.toString())
    }
  }, [partners])

  const handlePickPartner = async (relnr: string) => {
    try {
      if (selected_partner !== relnr) setselected_customer(null)
      setselected_partner(relnr)
      setcustomers_list(null)
      setis_loading_customers(true)
      const list = await getCustomerList(auth, relnr)
      setcustomers_list(list ?? null)
      if (list?.length === 1) {
        setselected_customer(list[0].relnr.toString())
      }
    } catch (error) {
      console.log(error)
      toast.error(t('settings.toast_error_p2'), { id: 'customers' })
    } finally {
      setis_loading_customers(false)
    }
  }

  const partnersArr = Array.isArray(partners)
    ? partners.map((item) => ({ value: item.relnr, label: `${item.name} - ${item.relnr}` }))
    : []

  const customersArr = Array.isArray(customers_list)
    ? customers_list.map((item) => ({
        value: item.relnr,
        label: `${item.name} - ${item.clientcode || item.relnr}`
      }))
    : []

  return (
    <>
      <Routes>
        <Route path=":customerid" element={<Detail />} />
        <Route path="" element={
          <>
            <Toaster />
            <div className='overflow-auto relative w-full p-4 md:p-8'>
              <Breadcrumb items={[
                { title: <p onClick={() => { navigate('/instellingen') }} className='cursor-pointer'>{t('settings.title')}</p> },
                { title: t('settings.customer_settings_title') }
              ]} />
              <h1 className='text-2xl font-semibold'>{t('settings.customer_settings_title')}</h1>

              <div className='flex flex-col gap-4 p-4 shadow-sm border rounded-sm mt-4 md:mt-8'>
                <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4'>
                  <div>
                    <Label htmlFor='partner'>{t('settings.partner')}</Label>
                    <Combobox
                      id='partner'
                      disabled={Array.isArray(partners) && partners.length === 1}
                      selectedValue={selected_partner ?? undefined}
                      onValueChange={(value) => { handlePickPartner(value.value.toString()) }}
                      options={partnersArr}
                      placeholder={t('settings.partner_placeholder')}
                      input_placeholder={t('settings.search_placeholder')}
                    />
                  </div>
                  <div>
                    <Label htmlFor='customer'>{t('settings.customer')}</Label>
                    <Combobox
                      id='customer'
                      disabled={!Array.isArray(customers_list) || is_loading_customers}
                      selectedValue={selected_customer ?? undefined}
                      onValueChange={(value) => { setselected_customer(value.value.toString()) }}
                      options={customersArr}
                      placeholder={is_loading_customers ? t('settings.search_placeholder') : t('settings.customer_settings_placeholder')}
                      input_placeholder={t('settings.search_placeholder')}
                    />
                  </div>
                </div>

                <Button
                  onClick={() => {
                    if (selected_customer) {
                      const found = customers_list?.find(c => c.relnr.toString() === selected_customer)
                      navigate(selected_customer, { state: { customer: found ?? null } })
                    }
                  }}
                  disabled={!selected_customer || is_loading_customers}
                  className='w-fit gap-2'
                >
                  {is_loading_customers ? <Loader2 className='animate-spin' /> : t('settings.search')}
                </Button>
              </div>
            </div>
          </>
        } />
      </Routes>
    </>
  )
}

export default CustomerSettings
